import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isApiError } from '@noor/api';
import { toast } from '@noor/lib';
import {
  vendorBroadcastApi,
  vendorBroadcastKeys,
  type RespondToBroadcastInput,
} from '@/entities/vendor-broadcast';

export interface RespondToBroadcastArgs extends RespondToBroadcastInput {
  id: number;
}

/**
 * The card's Accept/Reject transition.
 *
 * NOT optimistic, deliberately — the same rule as `useChangeOrderStatus`: the
 * backend arbitrates the transition and refuses it with 409 once `expires_at`
 * lapses, so painting the target status immediately would show an outcome the
 * server may reject. The button's pending state covers the round trip, and the
 * 409 path toasts the expiry copy and refetches so the card flips to expired.
 */
export function useRespondToBroadcast() {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();

  return useMutation({
    // The toasts below replace the global MutationCache one.
    meta: { suppressErrorToast: true },
    mutationFn: ({ id, status, rejectReason }: RespondToBroadcastArgs) =>
      vendorBroadcastApi.respond(id, {
        status,
        ...(rejectReason === undefined ? {} : { rejectReason }),
      }),
    onSuccess: async (_data, { status }) => {
      await queryClient.invalidateQueries({ queryKey: vendorBroadcastKeys.all });
      toast.success(
        t(
          status === 'accepted'
            ? 'broadcasts.accept_success'
            : 'broadcasts.reject_success',
        ),
      );
    },
    onError: (error, { status }) => {
      if (
        isApiError(error) &&
        (error.status === 409 || error.code === 'broadcast_expired')
      ) {
        toast.error(t('broadcasts.expired_action_error'));
        void queryClient.invalidateQueries({ queryKey: vendorBroadcastKeys.all });
        return;
      }
      toast.error(
        t(status === 'accepted' ? 'broadcasts.accept_error' : 'broadcasts.reject_error'),
      );
    },
  });
}
